import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import apiOffers from 'api/offers';
import IPriceListTemplate from 'api/interfaces/offers/IPriceListTemplate';
import IUserGet from 'api/interfaces/users/IUserGet';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

import 'style/layout/modal/ModalConfirm.css';

/**
 * Saves the additional price list of the offer to the registry, as a new list of the
 * user's department or over a list the user has made. A department can give its new offers
 * the rows of a list in the registry.
 */
const SavePriceListTemplate = ({
  additionalOffer,
  isOpen,
  offerId,
  toggle,
}: {
  additionalOffer: boolean;
  isOpen: boolean;
  offerId: number;
  toggle: () => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers]);
  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );

  const [own, setOwn] = useState<IPriceListTemplate[]>([]);
  const [overwrite, setOverwrite] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setName('');
    setOverwrite(false);
    setTemplateId('');
    apiOffers.priceListTemplates
      .get()
      .then((lists) =>
        setOwn(lists.filter((list) => list.createdUserId === currentUser?.id))
      )
      .catch(() => setOwn([]));
  }, [isOpen]);

  const canSave = overwrite ? templateId !== '' : name.trim() !== '';

  const save = async () => {
    if (!canSave || saving) {
      return;
    }

    setSaving(true);
    try {
      await apiOffers.priceListTemplates.post({
        additionalOfferId: additionalOffer ? offerId : undefined,
        name: name.trim(),
        offerId: additionalOffer ? undefined : offerId,
        templateId: overwrite ? parseInt(templateId, 10) : null,
      });
      dispatch(addMessage(t(`${translations.offers}:priceListRegistry.Saved`)));
      toggle();
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:priceListRegistry.Save failed`))
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader className="modal-header" toggle={toggle}>
        {t(`${translations.offers}:priceListRegistry.Title`)}
      </ModalHeader>
      <ModalBody>
        <p>{t(`${translations.offers}:priceListRegistry.Info`)}</p>
        <FormGroup check>
          <Label check>
            <Input
              checked={!overwrite}
              name="price-list-registry-target"
              onChange={() => setOverwrite(false)}
              type="radio"
            />{' '}
            {t(`${translations.offers}:priceListRegistry.New`)}
          </Label>
        </FormGroup>
        {overwrite ? null : (
          <FormGroup className="mt-2">
            <Label for="price-list-registry-name">
              {t(`${translations.offers}:priceListRegistry.Name`)}
            </Label>
            {/* The name is typed first, so the keys never go to the save button */}
            <Input
              autoFocus
              id="price-list-registry-name"
              onChange={(event) => setName(event.target.value)}
              value={name}
            />
          </FormGroup>
        )}
        {own.length === 0 ? null : (
          <>
            <FormGroup check className="mt-2">
              <Label check>
                <Input
                  checked={overwrite}
                  name="price-list-registry-target"
                  onChange={() => setOverwrite(true)}
                  type="radio"
                />{' '}
                {t(`${translations.offers}:priceListRegistry.Overwrite`)}
              </Label>
            </FormGroup>
            {overwrite ? (
              <Input
                aria-label={t(
                  `${translations.offers}:priceListRegistry.Overwrite`
                )}
                className="mt-2"
                onChange={(event) => setTemplateId(event.target.value)}
                type="select"
                value={templateId}
              >
                <option value="">
                  {t(`${translations.offers}:priceListRegistry.Choose`)}
                </option>
                {own.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name}
                  </option>
                ))}
              </Input>
            ) : null}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          className="modal-button"
          disabled={!canSave || saving}
          onClick={save}
        >
          {t(`${translations.offers}:priceListRegistry.Save`)}
        </Button>
        <Button className="modal-button" onClick={toggle}>
          {t(`${translations.offers}:priceListRegistry.Cancel`)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SavePriceListTemplate;
